import { useContext, useEffect, useMemo } from 'react'
import moment from 'moment'

import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlights } from 'actions/FlightActions'
import CurrencyContext from 'contexts/currencyContext'
import { getFlightSearchKey } from 'lib/flights/flightUtils'
import useOffers from 'hooks/Offers/useOffers'
import useOfferList from 'hooks/Offers/useOfferList'
import { HOTEL_OFFERS_LIMIT, MALDIVES_CHARTER_CONFIG } from 'constants/charterFlight'
import Places from 'constants/places'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { FlightsClassTypes } from 'constants/flight'
import { getWeekdaysByMonth, buildFlightSearchPayload, buildBundleOfferFilters, Weekday } from './MaldivesDirectUtils'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import getObjectKey from 'lib/object/getObjectKey'

interface DateRange {
  checkIn: string
  checkOut: string
}

export interface HotelDataByDate {
  fetching: boolean
  lowestPrice?: number
  lowestMemberPrice?: number
  offerCount?: number
}

interface FlightDataByDate {
  fetching: boolean
  pricePerPerson?: number
  totalPrice?: number
  journey?: App.JourneyV2
}

interface FlightCalendarData {
  itemBundleDataByDate: Record<string, CalendarItemBundleData>
}

interface UseBundleFlightDataParams {
  rooms: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  fareClass: FlightsClassTypes
  currency: string
}

interface BundleFlightData {
  flightDataByDate: Record<string, FlightDataByDate>
}

interface UseBundleOfferDataParams {
  offer: App.HotelOffer
  rooms: Array<App.Occupants>
  dateRangesByMonth: Record<string, Array<DateRange>>
  nights: number
  multipleRoomBookingActive?: boolean
}

interface BundleOfferData {
  hotelDataByDate: Record<string, HotelDataByDate>
  // For multiple room: aggregated data across all rooms
  multiRoomData?: {
    roomsData: Record<string, HotelDataByDate>
    isFetching: boolean
  }
}

interface UseOfferCharterCalendarDataParams {
  offer: App.HotelOffer
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  dateRangesByMonth: Record<string, Array<DateRange>>
  multipleRoomBookingActive?: boolean
  charterBundleRoute?: App.CharterBundleRoute
}

export interface CalendarItemBundleData {
  fetching: boolean
  available: boolean
  totalPrice: number
  pricePerPerson: number
  memberTotalPrice?: number
  memberPricePerPerson?: number
  hotelPrice?: number
  hotelMemberPrice?: number
  flightPrice?: number
  offerCount: number
  seatsLeft: number
}

/**
 * Custom hook to get date ranges grouped by month
 *
 * This hook generates checkIn/checkOut date pairs for a specific weekday
 * across multiple future months, grouped by month name.
 *
 * @param weekday - The departure weekday (0-6, where 0 = Sunday)
 * @param numberOfMonths - Number of future months to include
 * @param nights - Number of nights for the stay
 * @returns Record of month names to arrays of date ranges
 *
 * @example
 * const dateRanges = useDateRangesByMonth(Weekday.Saturday, 6, 5)
 * // Returns: {
 * //   'Oct': [
 * //     { checkIn: '2024-10-26', checkOut: '2024-10-31' },
 * //     { checkIn: '2024-11-02', checkOut: '2024-11-07' }
 * //   ],
 * //   'Nov': [...],
 * //   ...
 * // }
 */
export function useDateRangesByMonth(
  weekday: Weekday,
  numberOfMonths: number,
  nights: number
): Record<string, Array<DateRange>> {
  return useMemo(() => {
    const checkInsByMonth = getWeekdaysByMonth(weekday, numberOfMonths)
    const dateRangesByMonth: Record<string, Array<DateRange>> = {}

    Object.entries(checkInsByMonth).forEach(([monthKey, checkInDates]) => {
      dateRangesByMonth[monthKey] = checkInDates.map(checkIn => ({
        checkIn,
        checkOut: moment(checkIn).add(nights, 'days').format(ISO_DATE_FORMAT),
      }))
    })

    return dateRangesByMonth
  }, [weekday, numberOfMonths, nights])
}

/**
 * Custom hook to fetch flight data for a single calendar item
 *
 * @param params - Flight search parameters
 * @returns Flight data including fetching state, prices, and journey
 */
export function useFlightItemData(params: {
  checkIn: string
  checkOut: string
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
}) {
  const { checkIn, checkOut, rooms, fareClass } = params

  const dispatch = useAppDispatch()
  const currencyContext = useContext(CurrencyContext)
  const currency: string = currencyContext || 'AUD'

  // Fetch MEL → MAL flights
  useEffect(() => {
    const flightParams = buildFlightSearchPayload({
      departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT, // MEL
      arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT, // MAL
      departureDate: checkIn,
      returnDate: checkOut,
      occupancies: rooms,
      fareClass: fareClass,
      currency,
    })
    dispatch(fetchFlights(flightParams))
  }, [dispatch, checkIn, checkOut, rooms, fareClass, currency])

  // Get flight data from Redux
  const flightSearchKey = useMemo(() => {
    const flightParams = buildFlightSearchPayload({
      departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
      arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
      departureDate: checkIn,
      returnDate: checkOut,
      occupancies: rooms,
      fareClass: fareClass,
      currency,
    })
    return getFlightSearchKey(flightParams)
  }, [checkIn, checkOut, rooms, fareClass, currency])

  const flightData = useAppSelector(state => state.flights.searchV2Flights[flightSearchKey])

  // Calculate flight prices
  const flightPricing = useMemo(() => {
    const departingFlight = flightData?.fares?.[0]?.[0]
    const returningFlight = flightData?.fares?.[1]?.[0]
    const departingFare = departingFlight?.price?.all?.totalFare ?? 0
    const returningFare = returningFlight?.price?.all?.totalFare ?? 0
    const totalFlightFare = departingFare + returningFare

    return {
      fetching: flightData?.fetching ?? true,
      totalFlightFare,
      departingFlight,
    }
  }, [flightData])

  return flightPricing
}

/**
 * Lightweight hook for individual calendar item to fetch its own bundle data
 *
 * This hook is used by CalendarItem components to independently load:
 * - Maldives hotel offers (isStream) for specific dates
 * - MEL → MAL flights for specific dates
 * - Calculate bundle pricing directly from Redux state
 *
 * Key features:
 * - No useOffers dependency (reads directly from Redux)
 * - isStream for real-time offer data
 * - Independent data fetching per item
 *
 * @param params - Configuration for the specific date range
 * @returns Bundle data including pricing and availability
 */
export function useCalendarItemData(params: {
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
}): CalendarItemBundleData {
  const { checkIn, checkOut, nights, rooms, fareClass, priceMode } = params

  // Calculate total passengers
  const totalPassengers = useMemo(
    () => sumUpOccupancies(rooms),
    [rooms]
  )

  // Build hotel offer filters using shared utility function
  const hotelFilters: App.OfferListFilters = useMemo(() =>
    buildBundleOfferFilters({
      destinationId: Places.Maldives.id,
      checkIn: checkIn,
      checkOut: checkOut,
      rooms: rooms,
      limit: HOTEL_OFFERS_LIMIT,
      isPricePerPerson: priceMode === 'perPerson',
    }), [checkIn, checkOut, rooms, priceMode])

  // Use useOfferList hook - same as main search for consistency
  const offerList = useOfferList(hotelFilters, {
    clientCheckAvailability: true,
  })

  // Use useOffers to fetch complete offer data with packages
  const [offers, offersFetching] = useOffers<App.HotelOffer>(offerList.offerIds)

  // Use useFlightItemData to get flight data
  const flightItemData = useFlightItemData({
    checkIn,
    checkOut,
    rooms,
    fareClass,
  })

  // Calculate bundle pricing (from complete offer data with packages)
  const bundleData = useMemo(() => {
    const hotelFetching = offerList?.fetching || offersFetching
    const flightFetching = flightItemData.fetching

    // Extract prices from complete offer data (includes packages with duration)
    const prices: Array<number> = []
    const memberPrices: Array<number> = []

    offers.forEach(offer => {
      const matchingPackage = offer.packages.find(pkg => pkg.duration === nights)
      if (!matchingPackage) return

      if (matchingPackage.price > 0) {
        prices.push(matchingPackage.price)
      }
      if (matchingPackage.memberPrice && matchingPackage.memberPrice > 0) {
        memberPrices.push(matchingPackage.memberPrice)
      }
    })

    const lowestPrice = prices.length > 0 ? Math.min(...prices) : undefined
    const lowestMemberPrice = memberPrices.length > 0 ? Math.min(...memberPrices) : undefined

    // Get flight prices from useFlightItemData
    const totalFlightFare = flightItemData.totalFlightFare

    // Calculate bundle prices
    const bothDataReady = !hotelFetching && !flightFetching && lowestPrice && totalFlightFare > 0

    let bundleTotalPrice: number | undefined
    let bundleMemberTotalPrice: number | undefined
    let bundlePricePerPerson: number | undefined
    let bundleMemberPricePerPerson: number | undefined

    if (bothDataReady) {
      bundleTotalPrice = lowestPrice + totalFlightFare
      bundlePricePerPerson = totalPassengers > 0 ? bundleTotalPrice / totalPassengers : undefined

      if (lowestMemberPrice) {
        bundleMemberTotalPrice = lowestMemberPrice + totalFlightFare
        bundleMemberPricePerPerson = totalPassengers > 0 ? bundleMemberTotalPrice / totalPassengers : undefined
      }
    }

    return {
      fetching: hotelFetching || flightFetching,
      available: !!bothDataReady,
      totalPrice: bundleTotalPrice ?? 0,
      pricePerPerson: bundlePricePerPerson ?? 0,
      memberTotalPrice: bundleMemberTotalPrice,
      memberPricePerPerson: bundleMemberPricePerPerson,
      hotelPrice: lowestPrice,
      hotelMemberPrice: lowestMemberPrice,
      flightPrice: totalFlightFare,
      offerCount: offerList.offerIds.length,
      seatsLeft: 5, // TODO: backend add available seats
    }
  }, [offerList, offers, flightItemData, nights, totalPassengers, offersFetching])

  return bundleData
}

/**
 * Custom hook to handle flight-only data
 *
 * This hook handles:
 * - Prefetching flight data for all date ranges
 * - Calculating flight prices (total and per person)
 * - Managing total passenger count
 * - Bundle calculation is done in the parent hook
 *
 * @param params - Configuration parameters including dateRangesByMonth
 * @returns Flight data by date and total passengers
 */
function useBundleFlightData(params: UseBundleFlightDataParams): BundleFlightData {
  const {
    rooms,
    dateRangesByMonth,
    fareClass,
    currency,
  } = params

  const dispatch = useAppDispatch()

  // Serialize rooms and dates to prevent infinite loops from array reference changes
  const roomsKey = useMemo(() => getObjectKey({ rooms }), [rooms])
  const dateRangesKey = useMemo(() => getObjectKey(dateRangesByMonth), [dateRangesByMonth])

  // Prefetch flight data for all departure dates
  useEffect(() => {
    Object.values(dateRangesByMonth).flat().forEach(({ checkIn, checkOut }) => {
      const searchParams = buildFlightSearchPayload({
        departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
        arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
        departureDate: checkIn,
        returnDate: checkOut,
        occupancies: rooms,
        fareClass: fareClass,
        currency,
      })

      dispatch(fetchFlights(searchParams))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangesKey, currency, roomsKey, fareClass])

  // Get all flight search results from Redux
  const allFlightSearches = useAppSelector(state => state.flights.searchV2Flights)

  // Map flight search results - only flight data, no bundle calculation
  const flightDataByDate = useMemo(() => {
    const dataMap: Record<string, FlightDataByDate> = {}

    Object.values(dateRangesByMonth).flat().forEach(({ checkIn, checkOut }) => {
      const searchParams = buildFlightSearchPayload({
        departureAirport: MALDIVES_CHARTER_CONFIG.DEPARTURE_AIRPORT,
        arrivalAirport: MALDIVES_CHARTER_CONFIG.ARRIVAL_AIRPORT,
        departureDate: checkIn,
        returnDate: checkOut,
        occupancies: rooms,
        fareClass: fareClass,
        currency,
      })

      const searchKey = getFlightSearchKey(searchParams)
      const flightData = allFlightSearches[searchKey]

      if (flightData) {
        const departingFlight = flightData.fares?.[0]?.[0]
        const returningFlight = flightData.fares?.[1]?.[0]

        // Calculate round-trip total fare
        const departingFare = departingFlight?.price?.all?.totalFare ?? 0
        const returningFare = returningFlight?.price?.all?.totalFare ?? 0
        const totalFare = departingFare + returningFare

        dataMap[checkIn] = {
          fetching: Boolean(flightData.fetching),
          totalPrice: totalFare,
          journey: departingFlight,
        }
      } else {
        dataMap[checkIn] = {
          fetching: true,
        }
      }
    })

    return dataMap
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRangesKey, roomsKey, currency, fareClass, allFlightSearches])

  return {
    flightDataByDate,
  }
}

/**
 * Custom hook to handle single offer calendar data
 *
 * This hook is a pure data consumer that reads calendar data from Redux.
 * It does NOT dispatch any fetch actions - it relies on the parent component
 * (e.g., FlashHotelContent.fetchCalendarData) to fetch the data.
 *
 * This hook handles:
 * - Reading calendar data for a specific offer from Redux
 * - Extracting hotel prices for date ranges from calendar
 * - Optimized for single offer (not offer lists)
 *
 * @param params - Configuration parameters including dateRangesByMonth
 * @returns BundleOfferData object containing hotel data for the date ranges
 */
function useBundleOfferData(params: UseBundleOfferDataParams): BundleOfferData {
  const {
    offer,
    rooms,
    dateRangesByMonth,
    multipleRoomBookingActive = false,
    nights,
  } = params

  // Get the package for the current duration
  const currentPackage = useMemo(() => {
    return offer.packages.find(pkg => pkg.duration === nights)
  }, [offer.packages, nights])

  // Get calendar data from Redux (data should be fetched by parent component)
  const calendarData = useAppSelector(state => {
    if (!currentPackage) return null

    if (multipleRoomBookingActive) {
      // For multiple room, we need to aggregate data from all occupancy calendars
      // This will be handled in the hotelDataByDate calculation below
      return null
    } else {
      // Single room: get from calendarsByPackageKey
      return state.calendar.calendarsByPackageKey[currentPackage.uniqueKey]
    }
  })

  // Get multiple room calendar data from Redux (data should be fetched by parent component)
  const multiRoomCalendarData = useAppSelector(state => {
    if (!multipleRoomBookingActive || !currentPackage) return null

    const occupancyCalendars: Record<string, App.Calendar> = {}
    let isAnyFetching = false

    rooms.forEach(room => {
      const occupancyKey = generateOccupancyStringByRoom(room)
      const occupancyCalendar = state.calendar.calendarsByOccupancy[occupancyKey]?.[currentPackage.uniqueKey]
      const isFetching = state.calendar.calendarsByOccupancyLoading[occupancyKey]?.[currentPackage.uniqueKey] ?? false

      if (occupancyCalendar) {
        occupancyCalendars[occupancyKey] = occupancyCalendar
      }
      if (isFetching) {
        isAnyFetching = true
      }
    })

    return {
      calendars: occupancyCalendars,
      isFetching: isAnyFetching,
    }
  }, shallowEqual)

  // Extract hotel data for charter weekdays only
  const hotelDataByDate = useMemo(() => {
    const dataMap: Record<string, HotelDataByDate> = {}
    const allCheckInDates = Object.values(dateRangesByMonth).flat().map(range => range.checkIn)

    if (multipleRoomBookingActive) {
      // Multiple room: aggregate data from all occupancy calendars
      if (!multiRoomCalendarData) {
        // Still loading, mark all weekdays as fetching
        allCheckInDates.forEach(checkInDate => {
          dataMap[checkInDate] = {
            fetching: true,
          }
        })
        return dataMap
      }

      // Aggregate data across all rooms for each weekday
      allCheckInDates.forEach(checkInDate => {
        const roomPrices: Array<number> = []
        const roomMemberPrices: Array<number> = []
        let totalOfferCount = 0
        let isAnyRoomFetching = false

        rooms.forEach(room => {
          const occupancyKey = generateOccupancyStringByRoom(room)
          const occupancyCalendar = multiRoomCalendarData.calendars[occupancyKey]

          if (!occupancyCalendar) {
            isAnyRoomFetching = true
            return
          }

          // Find the day in this occupancy calendar
          occupancyCalendar.months.forEach(month => {
            month.days.forEach(day => {
              if (day.checkIn === checkInDate) {
                if (day.hotelAvailable) {
                  roomPrices.push(day.hotelTotal)
                  if (day.hotelMemberTotal) {
                    roomMemberPrices.push(day.hotelMemberTotal)
                  }
                  totalOfferCount += 1
                }
              }
            })
          })
        })

        // For multiple rooms, we need to SUM all room prices, not take the minimum
        // Each occupancy calendar returns the price for that specific room configuration
        const totalPrice = roomPrices.length > 0 ? roomPrices.reduce((sum, price) => sum + price, 0) : undefined
        const totalMemberPrice = roomMemberPrices.length > 0 ? roomMemberPrices.reduce((sum, price) => sum + price, 0) : undefined

        dataMap[checkInDate] = {
          fetching: multiRoomCalendarData.isFetching || isAnyRoomFetching,
          lowestPrice: totalPrice,
          lowestMemberPrice: totalMemberPrice,
          offerCount: totalOfferCount,
        }
      })
    } else {
      // Single room: use regular calendar data
      if (!calendarData) {
        // Calendar is still loading, mark all weekdays as fetching
        allCheckInDates.forEach(checkInDate => {
          dataMap[checkInDate] = {
            fetching: true,
          }
        })
        return dataMap
      }

      // Extract data from calendar months
      calendarData.months.forEach(month => {
        month.days.forEach(day => {
          // Only include dates that match our charter weekdays
          if (allCheckInDates.includes(day.checkIn)) {
            dataMap[day.checkIn] = {
              fetching: false,
              lowestPrice: day.hotelAvailable ? day.hotelTotal : undefined,
              lowestMemberPrice: day.hotelMemberTotal || undefined,
              offerCount: day.hotelAvailable ? 1 : 0,
            }
          }
        })
      })
    }

    // Fill in any missing weekdays (not in calendar response)
    allCheckInDates.forEach(checkInDate => {
      if (!dataMap[checkInDate]) {
        dataMap[checkInDate] = {
          fetching: false,
          lowestPrice: undefined,
          lowestMemberPrice: undefined,
          offerCount: 0,
        }
      }
    })

    return dataMap
  }, [calendarData, multiRoomCalendarData, dateRangesByMonth, multipleRoomBookingActive, rooms])

  return {
    hotelDataByDate
  }
}

/**
 * Custom hook to handle charter bundle calendar data for a single offer (FlashHotelContent)
 *
 * This hook is a pure data consumer that reads calendar and flight data from Redux.
 * It does NOT dispatch any fetch actions - it relies on the parent component
 * (e.g., FlashHotelContent.fetchCalendarData) to fetch calendar data.
 *
 * This hook handles:
 * - Accepting date ranges from caller
 * - Delegating offer calendar logic to useBundleOfferData (pure Redux reader)
 * - Delegating flight logic to useBundleFlightData (handles flight fetching)
 * - Combining hotel and flight data into bundle pricing
 * - Optimized for single offer pages (not search lists)
 *
 * @param params - Configuration parameters for offer charter calendar
 * @returns FlightCalendarData object containing bundle data for the offer
 */
export function useOfferCharterCalendarData(params: UseOfferCharterCalendarDataParams): FlightCalendarData {
  const {
    offer,
    nights,
    rooms,
    fareClass,
    dateRangesByMonth,
    multipleRoomBookingActive = false,
    charterBundleRoute,
  } = params

  const currencyContext = useContext(CurrencyContext)
  const currency: string = currencyContext || 'AUD'

  // Calculate total number of passengers
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])

  // Only process data if charterBundleRoute is present
  const shouldFetchData = !!charterBundleRoute

  // Get offer calendar data from Redux (pure reader, no dispatch)
  const { hotelDataByDate } = useBundleOfferData({
    offer,
    nights,
    rooms,
    dateRangesByMonth: shouldFetchData ? dateRangesByMonth : {},
    multipleRoomBookingActive,
  })

  // Get flight data using the dedicated hook
  const { flightDataByDate } = useBundleFlightData({
    rooms,
    dateRangesByMonth: shouldFetchData ? dateRangesByMonth : {},
    fareClass,
    currency,
  })

  // Build ready-to-use calendar item data for all dates
  const itemBundleDataByDate = useMemo(() => {
    const dataMap: Record<string, CalendarItemBundleData> = {}

    Object.values(dateRangesByMonth).flat().forEach(({ checkIn }) => {
      const flightData = flightDataByDate[checkIn]
      const hotelData = hotelDataByDate[checkIn]

      // Calculate bundle prices
      const bothDataReady = !!(hotelData?.lowestPrice && flightData?.totalPrice)
      const bundleTotalPrice = bothDataReady ? (hotelData.lowestPrice! + flightData.totalPrice!) : 0
      const bundlePricePerPerson = bothDataReady && totalPassengers > 0
        ? bundleTotalPrice / totalPassengers
        : 0

      const bundleMemberTotalPrice = bothDataReady && hotelData.lowestMemberPrice && flightData.totalPrice
        ? hotelData.lowestMemberPrice + flightData.totalPrice
        : undefined
      const bundleMemberPricePerPerson = bundleMemberTotalPrice && totalPassengers > 0
        ? bundleMemberTotalPrice / totalPassengers
        : undefined

      dataMap[checkIn] = {
        fetching: flightData?.fetching || hotelData?.fetching || false,
        available: bothDataReady,
        totalPrice: bundleTotalPrice,
        pricePerPerson: bundlePricePerPerson,
        memberTotalPrice: bundleMemberTotalPrice,
        memberPricePerPerson: bundleMemberPricePerPerson,
        hotelPrice: hotelData?.lowestPrice,
        hotelMemberPrice: hotelData?.lowestMemberPrice,
        flightPrice: flightData?.totalPrice,
        offerCount: hotelData?.offerCount ?? 0,
        seatsLeft: 5, // TODO: get from backend
      }
    })

    return dataMap
  }, [dateRangesByMonth, flightDataByDate, hotelDataByDate, totalPassengers])

  return {
    itemBundleDataByDate,
  }
}
